import { CircularProgress, LinearProgress, ThemeProvider } from '@material-ui/core';
import React, { Fragment, useEffect, useState } from 'react';
import {
  addVariant,
  calculateSubtotalForRewards,
  changeItemQuantity,
  isLoopReturnsActive,
  useCartData,
} from '../../../../../utils/cart';
import globalMaterialUITheme from '../../../../../../@alo/yoga-ui';
import { observer } from 'mobx-react-lite';
import { GWPstate, Gift } from './gwpState';
import { toJS } from 'mobx';
import { fireHeapRedeemGWPevent } from '../../../../../utils/heap-events';
import useElementSize from '../../../../../templates/_shared/react-hooks';
import { memorizeGiftRemoval } from './gwpUtils';
import { fireHeapRemovalGWPevent } from '../../../../../utils/heap-events';
import './gwp-styles.scss';
import { toLocalCurrencyShowEstimate } from 'src/scripts/utils/currency';

const serverCartData: WindowExtended.ServerCartData | undefined = window.server_cart_data;

let Gifts = ({ isNewDesign = false, ATBtype = 'Cart GWP Carousel' }) => {
  const gifts = toJS(GWPstate.currentGifts);
  const cartData = useCartData();
  const { giftItemsAndLoyaltyRewards } = cartData;
  const subtotalForRewards = calculateSubtotalForRewards(cartData);
  const loyaltyGiftItemsVariantIDsInCart = giftItemsAndLoyaltyRewards.flatMap((item) => item.variant_id);
  const loyaltyGiftItemsKeyInCart = giftItemsAndLoyaltyRewards.flatMap((item) => item.key);
  const [customerData, setCustomerData] = useState<CustomerInfo | null>(null);

  const giftsInCartCount = giftItemsAndLoyaltyRewards.filter((item) => item.properties._isGWP).length;
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (window.getCustomerInfo) {
      setCustomerData(window.getCustomerInfo());
    }
  }, []);

  const [containerRef, { width: gwpWidth, scrollWidth: gwpScrollWidth }] = useElementSize();

  return !isLoopReturnsActive() && gifts && gifts.length > 0 ? (
    <div
      className={`gwp ${isNewDesign ? 'new-design' : ''} ${
        customerData?.isCustomer || (gifts && gifts.length > 0) ? 'customer' : ''
      } ${serverCartData?.settings.hide_gwp_carousel ? 'd-none' : ''}`}
    >
      <div className="hero">
        <div className="copy-column">
          <h6 className="title h6 uppercase">
            {isNewDesign ? `Free Gifts Available` : 'A Free Gift For You'}
            {isNewDesign && giftsInCartCount > 0 && (
              <span style={{ fontWeight: 300, lineHeight: 1 }}> {`(${giftsInCartCount} added)`}</span>
            )}
          </h6>
        </div>
      </div>
      {gifts && gifts.length > 0 ? (
        <div className={`${gwpScrollWidth > gwpWidth && !isNewDesign ? 'gwp-custom-scroll' : ''}`}>
          <div ref={containerRef} className="gwp-tiles">
            {gifts.map((gift) => (
              <GiftTile
                gift={gift}
                subtotalForRewards={subtotalForRewards}
                loyaltyGiftItemsVariantIDsInCart={loyaltyGiftItemsVariantIDsInCart}
                loyaltyGiftItemsKeyInCart={loyaltyGiftItemsKeyInCart}
                isNewDesign={isNewDesign}
                ATBtype={ATBtype}
                isLoading={isLoading}
                setIsLoading={setIsLoading}
              />
            ))}
          </div>
        </div>
      ) : null}
    </div>
  ) : null;
};

Gifts = observer(Gifts);
export { Gifts };

const GiftTile = ({
  gift,
  subtotalForRewards,
  loyaltyGiftItemsVariantIDsInCart,
  loyaltyGiftItemsKeyInCart,
  isNewDesign = false,
  ATBtype = 'Cart GWP Carousel',
  isLoading = false,
  setIsLoading,
}: {
  gift: Gift;
  subtotalForRewards: number;
  loyaltyGiftItemsVariantIDsInCart: number[] | null;
  loyaltyGiftItemsKeyInCart: string[] | null;
  isNewDesign?: boolean;
  ATBtype?: string;
  isLoading?: boolean;
  setIsLoading: (loading: boolean) => void;
}) => {
  const alreadyRedeemed = loyaltyGiftItemsVariantIDsInCart?.includes(Number(gift.variant_id));
  const isRedeemable = gift.amount_to_spend <= subtotalForRewards || gift.amount_to_spend === 0;
  const itemsKeyInCart = loyaltyGiftItemsKeyInCart?.filter((keyVarientId) =>
    String(keyVarientId)?.includes(String(gift.variant_id))
  );

  const progressValue = (subtotalForRewards / gift.amount_to_spend) * 100;
  const addVariantItems = async () => {
    await addVariant(gift.variant_id, null, { shouldShowATCmodal: false, ATBtype }, undefined, {
      _isGWP: true,
    }).then((data) => {
      // If success
      if (data?.data?.items) {
        fireHeapRedeemGWPevent({
          productTitle: gift.title,
          variantId: gift.variant_id,
          autoRedeemed: false,
        });
      }
    });
  };
  const removeVariantItems = async () => {
    memorizeGiftRemoval(Number(gift.variant_id));
    await changeItemQuantity(String(itemsKeyInCart), 0, false, { _isGWP: true });
    fireHeapRemovalGWPevent({ productTitle: gift.title, variantId: gift.variant_id.toString() });
  };

  const ATCcopy = isNewDesign ? 'ADD TO BAG' : 'GET THIS FREE';
  const cta = (
    <Fragment key="cta">
      {!isRedeemable && !alreadyRedeemed ? (
        <>
          <span>{gift.text_before_amount} </span>
          <span>{`${toLocalCurrencyShowEstimate(gift.amount_to_spend - subtotalForRewards)}`}</span>
          <span> {gift.text_after_amount}</span>
        </>
      ) : isRedeemable && alreadyRedeemed ? (
        <>
          <span>Your FREE </span>
          <span>{gift.title}</span>
          <span> was added to your bag</span>
        </>
      ) : isRedeemable ? (
        <span>{gift.subtext_once_redeemable}</span>
      ) : null}
    </Fragment>
  );

  return (
    <div className="gwp-tile" style={alreadyRedeemed ? { borderColor: 'var(--highlight-medium)' } : {}}>
      {isNewDesign && (
        <div className="progress-bar">
          {alreadyRedeemed ? (
            <span className="alo-checkmark-in-circle-highlight-medium-icon"></span>
          ) : (
            <span className="alo-circle-alto-icon"></span>
          )}
          <LinearProgress variant="determinate" value={progressValue >= 100 ? 100 : progressValue} />
        </div>
      )}
      <div className="tile-info-wrapper">
        <div className="image-wrapper">
          <img src={gift.image} className="gwp-image" alt={gift.image_alt_text} />
          {!isNewDesign && (
            <ThemeProvider theme={globalMaterialUITheme}>
              <CircularProgress
                variant="static"
                color="primary"
                size={74}
                thickness={1.5}
                value={progressValue >= 100 ? 100 : progressValue}
                disableShrink
              />
            </ThemeProvider>
          )}
        </div>
        <div className="gwp-info">
          <div className="gwp-text-container">
            <p className="gwp-title body semibold">{isNewDesign ? cta : gift.title}</p>
            <p className={`gwp-subtitle body-small ${isRedeemable}`}>{isNewDesign ? gift.title : cta}</p>
          </div>
          <div>
            <button
              className={`redeem-btn ${alreadyRedeemed ? 'remove-redeem-btn' : ''}`}
              type="button"
              disabled={!isRedeemable || isLoading}
              onClick={async () => {
                setIsLoading(true);
                alreadyRedeemed ? await removeVariantItems() : await addVariantItems();
                setIsLoading(false);
              }}
            >
              {alreadyRedeemed
                ? 'Remove'
                : isRedeemable
                ? ATCcopy
                : `Add ${toLocalCurrencyShowEstimate(gift.amount_to_spend - subtotalForRewards)} more`}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
